/**
 * Maps raw backend / provider error messages into user-facing copy.
 *
 * Background: `generations.error_message` can be anything that bubbled up
 * from the provider pipeline — including operator-grade traces like
 *
 *   "pipeline_kickoff: All provider keys exhausted for kind='image'.
 *    Last error from kie: key_revoked — Unauthorized – Authentication
 *    failed. Please check that your Authorization and Content-Type
 *    headers are correctly set."
 *
 * Showing that verbatim is bad UX (and arguably a security/posture leak:
 * leaks internal provider names + auth-header hints). We categorize on
 * the client by regex patterns and surface a short, actionable message.
 *
 * The raw string is preserved server-side for audit/support — we never
 * destructively transform it, only translate for display.
 *
 * NOTE: pattern matching on free-text errors is fragile. The clean fix is
 * a structured `error_code` enum returned by the API
 * (see spawn_task in the parent session). Until that lands, treat this
 * file as the single point of truth for "what does this error mean to a
 * human" and add new patterns as we observe new failure modes in beta.
 */

export type GenerationErrorCategory =
  | 'no_face'
  | 'sunglasses'
  | 'content_policy'
  | 'provider_outage'
  | 'rate_limited'
  | 'timeout'
  | 'unknown';

export interface DisplayError {
  category: GenerationErrorCategory;
  /** Short heading — fits one line on phone width. */
  title: string;
  /** 1-2 sentence guidance. What the user can actually do next. */
  hint: string;
}

/**
 * Default copy used when error_message is null OR doesn't match any known
 * pattern. "Unknown" is safe: tells user to retry without admitting we
 * don't know what happened (which would feel scary).
 */
const UNKNOWN: DisplayError = {
  category: 'unknown',
  title: "We couldn't finish your kiss",
  hint: 'Something went wrong on our side. Your stars are being refunded — please try again in a few minutes.',
};

/**
 * Patterns are checked in declaration order — most specific first. Once
 * one matches we return; we don't try to combine multiple.
 *
 * Provider-outage covers ALL backend-only failures (revoked key, quota
 * exhausted, vendor 5xx). User can't act, just retry later. We never
 * say "auth failed" or "key revoked" — those words belong to ops, not
 * users.
 */
const PATTERNS: Array<{ category: GenerationErrorCategory; re: RegExp; copy: DisplayError }> = [
  // ─── User-actionable failures ───────────────────────────────────
  {
    category: 'no_face',
    re: /no[\s_-]?face|face.{0,20}not.{0,10}(detect|found)|face.{0,10}detection.{0,10}fail/i,
    copy: {
      category: 'no_face',
      title: "We couldn't detect a face",
      hint: 'Our system needs a clear, centered face. Try a different photo with the face visible and well-lit.',
    },
  },
  {
    category: 'sunglasses',
    re: /sunglass|glasses.{0,20}(detect|cover)/i,
    copy: {
      category: 'sunglasses',
      title: 'Photo has sunglasses',
      hint: 'We work best with clear faces. Try a photo without sunglasses or with the eyes visible.',
    },
  },
  {
    category: 'content_policy',
    re: /content[\s_-]?(policy|filter|moderation)|safety[\s_-]?(filter|policy)|prohibited|unsafe|nsfw/i,
    copy: {
      category: 'content_policy',
      title: 'Photo could not be processed',
      hint: "This photo didn't pass our content checks. Try a different one — clear, well-lit portraits work best.",
    },
  },

  // ─── Operator / backend failures (user can't act) ──────────────
  {
    category: 'provider_outage',
    re: /provider keys exhausted|key[\s_-]?revoked|unauthorized|authentication[\s_-]?fail|invalid[\s_-]?api[\s_-]?key|forbidden/i,
    copy: {
      category: 'provider_outage',
      title: "We're having trouble right now",
      hint: "Our generation service is temporarily unavailable. Your stars have been refunded — please try again later.",
    },
  },
  {
    category: 'rate_limited',
    re: /rate[\s_-]?limit|too many request|429|quota[\s_-]?(exceeded|exhausted)/i,
    copy: {
      category: 'rate_limited',
      title: "We're at capacity",
      hint: 'Lots of people creating kisses right now. Your stars are refunded — try again in a few minutes.',
    },
  },
  {
    category: 'timeout',
    re: /timeout|timed[\s_-]?out|deadline|took too long/i,
    copy: {
      category: 'timeout',
      title: 'Generation took too long',
      hint: 'Your stars have been refunded. Please try again — the next attempt usually goes through faster.',
    },
  },
];

/**
 * Translate a raw error_message (or null) into user-facing copy.
 *
 * Returns the `UNKNOWN` fallback on null/empty/no-match — never throws.
 */
export function describeGenerationError(rawMessage: string | null | undefined): DisplayError {
  if (!rawMessage) return UNKNOWN;
  for (const p of PATTERNS) {
    if (p.re.test(rawMessage)) return p.copy;
  }
  return UNKNOWN;
}
